import { tableTokens, type SpacingToken } from '@/theme/tokens';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui/layout/Stack';
import { Box } from '@/ui/primitives';
import { Skeleton } from '@/ui/primitives';
import type { ReactElement } from 'react';

const SKELETON_DIMENSIONS = {
  titleWidth: 200,
  titleHeight: 28,
  cardHeight: 120,
  captionWidth: 160,
  rowHeight: tableTokens.rowHeight,
} as const;

type DataStateSkeletonProps = {
  variant?: 'table' | 'card' | 'flat';
  rows?: number;
  padding?: SpacingToken | null;
};

export const DataStateSkeleton = ({
  variant = 'table',
  rows = 5,
  padding,
}: DataStateSkeletonProps): ReactElement => {
  const gap: SpacingToken = variant === 'card' ? 'md' : 'sm';
  const fallbackPadding: SpacingToken = variant === 'card' ? 'md' : 'sm';
  const paddingToken = padding === null ? null : (padding ?? fallbackPadding);
  const paddingValue = paddingToken === null ? undefined : s(paddingToken);

  if (variant === 'card') {
    return (
      <Box sx={{ p: paddingValue }}>
        <Stack gap={gap}>
          <Skeleton
            variant="text"
            width={SKELETON_DIMENSIONS.titleWidth}
            height={SKELETON_DIMENSIONS.titleHeight}
          />
          <Skeleton variant="rounded" height={SKELETON_DIMENSIONS.cardHeight} />
          <Skeleton variant="text" width={SKELETON_DIMENSIONS.captionWidth} />
        </Stack>
      </Box>
    );
  }

  if (variant === 'flat') {
    return (
      <Box sx={{ p: paddingValue }}>
        <Stack gap={gap}>
          {Array.from({ length: rows }).map((_, idx) => (
            <Skeleton
              key={idx}
              variant="text"
              width={idx % 3 === 0 ? '60%' : idx % 3 === 1 ? '80%' : '50%'}
              height={SKELETON_DIMENSIONS.titleHeight}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: paddingValue }}>
      <Stack gap={gap}>
        {Array.from({ length: rows }).map((_, idx) => (
          <Skeleton
            key={idx}
            variant="rounded"
            height={idx === 0 ? SKELETON_DIMENSIONS.titleHeight : SKELETON_DIMENSIONS.rowHeight}
          />
        ))}
      </Stack>
    </Box>
  );
};
